import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Row,
  Col,
  message,
  Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
  createAdminOrder,
  fetch_coustomers,
  fetch_customer_details,
  updateOrderById,
  getOrderItemsByCustomer,
  createOrderItemNew,
  fetch_all_coupons,
} from "../../endpoints/API";
import AddOrderItemModal from "./AddOrderItemModal";
import PointOfContactModal from "./PointOfContactModal";
import dayjs from "dayjs";

const { Option } = Select;

function CreateOrder({ initialData, onSuccess }) {
  const [form] = Form.useForm();

  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [pointOfContact, setPointOfContact] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);

  const [addOrderItemVisible, setAddOrderItemVisible] = useState(false);
  const [isPOCModalVisible, setIsPOCModalVisible] = useState(false);

  const client = useSelector((state) => state.user.user);

  // Fetch customers and coupons
  useEffect(() => {
    const getCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const res = await fetch_coustomers();
        const couponRes = await fetch_all_coupons();
        setCustomers(res.data);
        setCoupons(couponRes?.data?.data || []);
      } catch (error) {
        message.error(
          error?.response?.data?.error || "Failed to fetch customers"
        );
      } finally {
        setLoadingCustomers(false);
      }
    };
    getCustomers();
  }, [client]);

  const processedCoupons = coupons.map((coupon) => {
    const now = new Date();
    const expired = new Date(coupon.expiry) < now;
    const minPurchaseMet =
      totalAmount >= parseFloat(coupon.minimum_purchase_amount);
    return { ...coupon, expired, minPurchaseMet };
  });

  // Customer change handler
  const handleCustomerChange = async (customerId) => {
    if (!customerId) {
      form.resetFields(["orderItems", "pointOfContact", "address", "coupon"]);
      setCustomerDetail(null);
      setPointOfContact([]);
      setAddresses([]);
      setOrderItems([]);
      setTotalAmount(0);
      setSelectedCoupon(null);
      setDiscountAmount(0);
      return [];
    }

    form.resetFields(["orderItems", "pointOfContact", "address"]);
    setTotalAmount(0);
    setSelectedCoupon(null);
    setDiscountAmount(0);

    try {
      const cusres = await fetch_customer_details(customerId);
      setCustomerDetail(cusres.data);

      setPointOfContact(cusres.data.point_of_contacts || []);
      setAddresses(cusres.data.location || []);

      if (cusres.data.point_of_contacts?.length) {
        form.setFieldValue(
          "pointOfContact",
          cusres.data.point_of_contacts[0].id
        );
      }
      if (cusres.data.location?.length) {
        form.setFieldValue("address", cusres.data.location[0].id);
      }

      const orderRes = await getOrderItemsByCustomer(customerId);
      setOrderItems(orderRes.data || []);

      return orderRes.data || [];
    } catch (error) {
      message.error(
        error?.response?.data?.error || "Failed to fetch customer details"
      );
      return [];
    }
  };

  // Prefill for edit mode
  useEffect(() => {
    if (initialData) {
      const prefill = async () => {
        const customerId = initialData.customer?.id;
        const pocId = initialData.point_of_contacts?.id;
        const addressId = initialData.location?.id;
        const schedule = initialData.schedule_datetime
          ? dayjs(initialData.schedule_datetime)
          : null;

        const customerOrderItems = customerId
          ? await handleCustomerChange(customerId)
          : [];

        const mappedOrderItems =
          initialData.order_item?.map((item) => ({
            id: item.id,
            test: item.test?.id || null,
            package: item.package?.id || null,
            familymember: item.familymember?.id,
            status: item.status,
          })) || [];

        form.setFieldsValue({
          customer: customerId,
          orderItems: mappedOrderItems.map((item) => item.id),
          pointOfContact: pocId,
          address: addressId,
          scheduleDate: schedule,
          coupon: initialData.coupon_applied?.id || null,
        });

        // Set selected coupon for edit
        setSelectedCoupon(initialData.coupon_applied || null);

        // Calculate total
        let total = 0;
        mappedOrderItems.forEach((item) => {
          if (item.test) {
            const testData = initialData.order_item.find(
              (i) => i.test?.id === item.test
            );
            total += Number(testData?.test?.b2c_price || 0);
          }
          if (item.package) {
            const packageData = initialData.order_item.find(
              (i) => i.package?.id === item.package
            );
            total += Number(packageData?.package?.b2c_price || 0);
          }
        });

        if (initialData.coupon_applied) {
          if (initialData.coupon_applied.discount_type === "money") {
            total -= Number(initialData.coupon_applied.discount_value || 0);
          } else if (initialData.coupon_applied.discount_type === "percent") {
            total -=
              total * (Number(initialData.coupon_applied.discount_value || 0) / 100);
          }
        }

        setTotalAmount(total >= 0 ? total : 0);
      };

      prefill();
    }
  }, [initialData]);

  // Save order
  const handleSaveOrder = async (values) => {
    const basePayload = {
      customer: values.customer,
      orderItems: values.orderItems,
      schedule_datetime: values.scheduleDate
        ? values.scheduleDate.format("YYYY-MM-DD HH:mm:ss")
        : null,
      location: values.address,
      coupon_applied: selectedCoupon?.id || null,
    };

    try {
      if (initialData) {
        // UPDATE → use point_of_contacts
        const updatePayload = {
          ...basePayload,
          point_of_contacts: values.pointOfContact,
        };

        await updateOrderById(initialData.id, updatePayload);
        message.success("Order updated!");
      } else {
        // CREATE → use point_of_contact
        const createPayload = {
          ...basePayload,
          point_of_contact: values.pointOfContact,
        };

        await createAdminOrder(createPayload);
        message.success("Order created!");
      }

      form.resetFields();
      setTotalAmount(0);
      setOrderItems([]);
      setSelectedCoupon(null);
      setDiscountAmount(0);
      onSuccess();

    } catch (error) {
      message.error(error?.response?.data?.error || "Failed to save order");
    }
  };

  // Add new order item manually
  const handleOrderItemCreate = async (values) => {
    try {
      const res = await createOrderItemNew({ ...values });

      let newItems = [];
      if (Array.isArray(res.data)) {
        newItems = res.data;
      } else if (Array.isArray(res.data.order_items)) {
        newItems = res.data.order_items;
      } else if (res.data) {
        newItems = [res.data];
      }

      setOrderItems((prev) => [...prev, ...newItems]);
      message.success("Order item added successfully!");
      setAddOrderItemVisible(false);
    } catch (error) {
      message.error(
        error?.response?.data?.error || "Failed to create order item"
      );
    }
  };

  // Handle order items selection change
  const handleOrderItemsChange = (selectedIds, appliedCoupon = null, discount = 0) => {
    const selectedItems = orderItems.filter((item) => selectedIds.includes(item.id));
    let total = selectedItems.reduce((sum, item) => {
      const price = item.price || item.test?.b2c_price || item.package?.b2c_price || 0;
      return sum + parseFloat(price);
    }, 0);

    if (appliedCoupon) {
      total -= discount;
      if (total < 0) total = 0;
    }

    setTotalAmount(total);
  };

  // Apply coupon
  const handleApplyCoupon = (coupon) => {
    if (!coupon.expired && coupon.minPurchaseMet) {
      setSelectedCoupon(coupon);
      setDiscountAmount(parseFloat(coupon.discount_value));
      form.setFieldValue("coupon", coupon.id);

      handleOrderItemsChange(
        form.getFieldValue("orderItems"),
        coupon,
        parseFloat(coupon.discount_value)
      );
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Form form={form} layout="vertical" onFinish={handleSaveOrder}>
        {/* Customer */}
        <Form.Item
          label="Customer"
          name="customer"
          rules={[{ required: true, message: "Please select a customer" }]}
        >
          <Select
            placeholder="Select Customer"
            loading={loadingCustomers}
            showSearch
            optionFilterProp="label"
            onChange={handleCustomerChange}
          >
            {customers.map((cust) => (
              <Option
                key={cust.id}
                value={cust.id}
                label={`${cust.first_name} ${cust.middle_name || ""} ${cust.last_name} (${cust.email} - ${cust.phone})`}
              >
                {cust.first_name} {cust.middle_name || ""} {cust.last_name} (
                {cust.email} - {cust.phone})
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Address */}
        <Form.Item
          label="Address"
          name="address"
          rules={[{ required: true, message: "Please select an Address" }]}
        >
          <Select placeholder="Select Address">
            {addresses.length > 0 ? (
              addresses.map((loc) => (
                <Option key={loc.id} value={loc.id}>
                  {loc.address_identifier} - {loc.city?.name}, {loc.state?.name}
                </Option>
              ))
            ) : (
              <Option disabled>No Address Available</Option>
            )}
          </Select>
        </Form.Item>

        {/* Order Items */}
        <Form.Item label="Order Items" required>
          <Row gutter={8}>
            <Col flex="auto">
              <Form.Item
                name="orderItems"
                rules={[{ required: true, message: "Please select Order Items" }]}
                noStyle
              >
                <Select
                  mode="multiple"
                  placeholder="Select Order Items"
                  onChange={handleOrderItemsChange}
                >
                  {orderItems.length > 0 ? (
                    orderItems.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.test?.name || item.package?.name_of_package || "Unnamed"} - (
                        {item.familymember?.first_name} {item.familymember?.last_name})
                      </Option>
                    ))
                  ) : (
                    <Option disabled>No Order Items Available</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Button icon={<PlusOutlined />} onClick={() => setAddOrderItemVisible(true)} />
            </Col>
          </Row>
        </Form.Item>

        {/* Point of Contact */}
        <Form.Item label="Point of Contact" required>
          <Row gutter={8}>
            <Col flex="auto">
              <Form.Item
                name="pointOfContact"
                rules={[{ required: true, message: "Please select a POC" }]}
                noStyle
              >
                <Select placeholder="Select POC">
                  {pointOfContact?.length ? (
                    pointOfContact.map((poc) => (
                      <Option key={poc.id} value={poc.id}>
                        {poc.first_name} {poc.last_name} ({poc.phone})
                      </Option>
                    ))
                  ) : (
                    <Option disabled>No Point of Contact</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Button icon={<PlusOutlined />} onClick={() => setIsPOCModalVisible(true)} />
            </Col>
          </Row>
        </Form.Item>

        {/* Schedule */}
        <Form.Item
          label="Schedule Order"
          name="scheduleDate"
          rules={[{ required: true, message: "Please select a Schedule Order Date" }]}
        >
          <DatePicker
            showTime
            disabledDate={(current) => current && current < dayjs().startOf("day")}
          />
        </Form.Item>

        {/* Coupons */}
        <Form.Item label="Coupons" name="coupon">
          <Select
            placeholder="Select a coupon"
            value={selectedCoupon?.id || undefined}
            style={{ width: "100%" }}
            dropdownRender={() => (
              <div style={{ maxHeight: 300, overflowY: "auto" }}>
                {processedCoupons.length ? (
                  processedCoupons.map((coupon) => (
                    <div
                      key={coupon.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: 8,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div>
                        <b>{coupon.code}</b> - Discount: {coupon.discount_value}{" "}
                        {coupon.expired ? (
                          <Tag color="red">Expired</Tag>
                        ) : coupon.minPurchaseMet ? (
                          <Tag color="green">Applicable</Tag>
                        ) : (
                          <Tag color="orange">
                            Min: {coupon.minimum_purchase_amount}
                          </Tag>
                        )}
                      </div>
                      {selectedCoupon?.id === coupon.id ? (
                        <Button
                          size="small"
                          danger
                          onClick={() => {
                            setSelectedCoupon(null);
                            setDiscountAmount(0);
                            form.setFieldValue("coupon", null);
                            handleOrderItemsChange(
                              form.getFieldValue("orderItems"),
                              null,
                              0
                            );
                          }}
                        >
                          ✕
                        </Button>
                      ) : (
                        <Button
                          type="default"
                          size="small"
                          disabled={coupon.expired || !coupon.minPurchaseMet}
                          onClick={() => handleApplyCoupon(coupon)}
                        >
                          Apply
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <span style={{ padding: 8 }}>No active coupons</span>
                )}
              </div>
            )}
          >
            {selectedCoupon && (
              <Option key={selectedCoupon.id} value={selectedCoupon.id}>
                {selectedCoupon.code} - Discount: {selectedCoupon.discount_value}
              </Option>
            )}
          </Select>
        </Form.Item>

        {/* Total */}
        <Form.Item label="Total Amount">
          <InputNumber value={totalAmount} readOnly style={{ width: "100%" }} />
        </Form.Item>

        <Button type="primary" htmlType="submit">
          {initialData ? "Update Order" : "Save Order"}
        </Button>
      </Form>

      {/* Add Order Item Modal */}
      <AddOrderItemModal
        visible={addOrderItemVisible}
        onClose={() => setAddOrderItemVisible(false)}
        clientId={client?.id}
        onAdd={handleOrderItemCreate}
        familyMembers={customerDetail?.family_member}
        createdBy={client?.user_id}
      />

      {/* POC Modal */}
      <PointOfContactModal
        visible={isPOCModalVisible}
        onClose={() => setIsPOCModalVisible(false)}
        customers={customers}
        onPOCAdded={(newPoc) => {
          setPointOfContact((prev) => [...prev, newPoc]);
          form.setFieldValue("pointOfContact", newPoc.id);
        }}
      />
    </div>
  );
}

export default CreateOrder;
